import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import SheetModal from './SheetModal';

/**
 * Sheet: slide-up panel for a single confirmation (Run brief, Choose vehicle).
 * Steps with inputs get their own screen instead (SPEC decision 14).
 * Props:
 * - isVisible, onClose (backdrop tap, swipe down, back button and the close button)
 * - title (string)
 * - children: sheet body (scrolls when taller than the screen)
 * - footer: pinned under the body, usually a PrimaryButton
 * - fullHeight (bool): fill most of the screen (long briefs, long vehicle lists)
 * - scrollRef: optional ref for the body ScrollView (e.g. to bring a selected option into view)
 * - scroll (default true): false renders the body in a plain View for content that
 *   brings its own virtualised list (FlatList); pair it with fullHeight for long lists
 * - style: extra style for the panel
 */
const Sheet = ({
	isVisible,
	onClose,
	title,
	children,
	footer,
	fullHeight = false,
	scrollRef,
	scroll = true,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const insets = useSafeAreaInsets();

	return (
		<SheetModal isVisible={isVisible} onClose={onClose} backdropColor={colors.overlay}>
			<View
				style={[
					styles.panel,
					fullHeight && styles.panelFull,
					{ paddingBottom: insets.bottom + 22 },
					style,
				]}
				accessibilityViewIsModal={true}
			>
				<View style={styles.handle} />
				<View style={styles.header}>
					<Text style={styles.title} accessibilityRole="header">{title}</Text>
					<Pressable
						onPress={onClose}
						accessibilityRole="button"
						accessibilityLabel="Close"
						style={({ pressed }) => [styles.closeButton, pressed && styles.pressed]}
					>
						<Feather name="x" size={18} color={colors.icon} />
					</Pressable>
				</View>
				{scroll ? (
					<ScrollView
						ref={scrollRef}
						style={fullHeight ? styles.bodyFull : styles.body}
						contentContainerStyle={styles.bodyContent}
						keyboardShouldPersistTaps="handled"
					>
						{children}
					</ScrollView>
				) : (
					<View style={[styles.bodyContent, fullHeight ? styles.bodyFull : styles.bodyStatic]}>
						{children}
					</View>
				)}
				{footer}
			</View>
		</SheetModal>
	);
};

const createStyles = ({ ui: { colors, radius, type, space, layout, elevation } }) => ({
	panel: {
		width: '100%',
		maxWidth: layout.maxContentWidth,
		alignSelf: 'center',
		maxHeight: '90%',
		paddingTop: 10,
		paddingHorizontal: space.gutter,
		gap: 14,
		borderTopLeftRadius: radius.sheet,
		borderTopRightRadius: radius.sheet,
		backgroundColor: colors.surface,
		...elevation.floating,
	},
	panelFull: {
		height: '90%',
	},
	handle: {
		width: 44,
		height: 5,
		borderRadius: 3,
		alignSelf: 'center',
		backgroundColor: colors.borderStrong,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.md,
	},
	title: {
		...type.cardTitle,
		color: colors.text,
		flex: 1,
	},
	closeButton: {
		width: 44,
		height: 44,
		borderRadius: 22,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: colors.raised,
	},
	pressed: {
		opacity: 0.8,
	},
	body: {
		flexGrow: 0,
	},
	bodyFull: {
		flex: 1,
	},
	bodyStatic: {
		flexShrink: 1,
	},
	bodyContent: {
		gap: space.sm,
	},
});

export default Sheet;
